/**
 * Typed client for the TargetQA API (FastAPI on Cloudflare Workers, /api/v1).
 * The bearer token lives in localStorage ("targetqa-token") and is attached automatically.
 */
export const API_URL = (
  process.env.NEXT_PUBLIC_API_URL ??
  process.env.NEXT_PUBLIC_PLATFORM_API_URL ??
  "https://api.targetqa.com"
).replace(/\/$/, "");
export const TOKEN_KEY = "targetqa-token";
export const USER_KEY = "targetqa-user";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface ApiUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_email_verified: boolean;
}

export function getToken(): string | null {
  return typeof window === "undefined" ? null : window.localStorage.getItem(TOKEN_KEY);
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
/** Called when the API rejects the stored token (expired / revoked). */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

const TRANSIENT_STATUS = new Set([502, 503, 504]);

export async function api<T>(path: string, init: RequestInit & { json?: unknown; auth?: boolean } = {}): Promise<T> {
  const { json, auth = true, headers, ...rest } = init;
  const token = auth ? getToken() : null;
  // Signed-out visitors: an authenticated endpoint can only answer 401, so fail
  // locally (same ApiError contract) instead of a wasted request + console error.
  if (auth && !token) {
    throw new ApiError(401, "NOT_AUTHENTICATED", "Please sign in to continue.");
  }
  const method = (rest.method ?? "GET").toUpperCase();
  // Safe to repeat: reads and idempotent writes. POST is never retried (could double-run/charge).
  const retryable = method === "GET" || method === "HEAD" || method === "PUT" || method === "DELETE";
  const attempts = retryable ? 3 : 1;
  let response: Response | undefined;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      response = await fetch(`${API_URL}/api/v1${path}`, {
        ...rest,
        headers: {
          "X-Client-App-Id": process.env.NEXT_PUBLIC_CLIENT_APP_ID || "targetqa",
          ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers,
        },
        body: json !== undefined ? JSON.stringify(json) : rest.body,
      });
      // A briefly overloaded edge returns 502/503/504 (often without CORS headers,
      // which the browser reports as a CORS/network error). Retry those transient ones.
      if (!TRANSIENT_STATUS.has(response.status) || attempt === attempts) break;
    } catch {
      if (attempt === attempts) {
        throw new ApiError(0, "NETWORK_ERROR", "TargetQA is temporarily busy. Please try again in a moment.");
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 300 * 3 ** (attempt - 1))); // 300ms, 900ms
  }
  if (!response) throw new ApiError(0, "NETWORK_ERROR", "TargetQA is temporarily busy. Please try again in a moment.");
  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    const detail = (body as { detail?: unknown } | null)?.detail;
    let message = error?.message ?? (typeof detail === "string" ? detail : Array.isArray(detail) ? "Please check the highlighted fields." : `Request failed (${response.status})`);
    if (response.status === 401 && token) {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(USER_KEY);
      }
      unauthorizedListeners.forEach((listener) => listener());
      if (message.toLowerCase().includes("token") || message.toLowerCase().includes("expired")) {
        message = "Your session has expired. Please sign in again.";
      }
    }
    throw new ApiError(response.status, error?.code ?? "HTTP_ERROR", message);
  }
  return body as T;
}
